/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/BoardColumn" />
import * as React from "react";
import { IObservableValue, ObservableValue, useObservable, useSubscription } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { MarkdownPreview } from "VSS/Features/MarkdownEditor/Components";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager, usePageContext, useService } from "VSS/Platform/Layout";
import { Button, IButtonProps } from "VSSUI/Button";
import { Icon, IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { css } from "VSSUI/Util";
import { BoardColumnType } from "Wit/BoardsHub/Common/Client/Contracts/Settings";
import { IObservableBoardsWorkItem } from "Wit/BoardsHub/Common/Interfaces";
import { BoardColumnLayout, BoardLayout } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { BoardCard } from "Wit/BoardsHub/KanbanBoard/Components/BoardCard";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { DragMode, IPosition, ScrollableData } from "Wit/Common/Controls/Boards/Draggable";
import { INewItemCalloutProps, NewItemCallout } from "Wit/Common/WorkItemControls/NewItemCallout";
import { FilterManager } from "Wit/Filter/FilterManager";
import { TextFilterProvider } from "Wit/Filter/TextFilterProvider";
import { WorkItemTypeColorAndIcon } from "Wit/UI/WorkItemTypeIcon";
import { IKanbanBoardService } from "../IKanbanBoardService";
import { isIncoming, isOutgoing } from "../Utils/BoardUtils";
import { ClickEditField } from "./ClickEditField";

export interface IMainBoardColumnProps
    extends IBoardColumnProps,
        Pick<BoardLayout, "backlogLevelWorkItemTypeColorAndIcons">,
        Pick<INewItemCalloutProps, "onAddItem"> {
    /**
     * Id of the board
     */
    boardId?: string;
    /**
     * Callback when the user clicks "Show more" when the column has partial work items
     */
    onPageAdditionalItems?: (column: BoardColumnLayout, filter?: string) => Promise<void>;
    /**
     * Wrap the column with some additional flex properties, used when there are multiple board rows
     */
    wrapColumn?: boolean;
    /**
     * current active workItem Types (available to create new item)
     */
    enabledWorkItemTypes: Set<string>;
    /**
     * user focus was changed to the Card in another column
     */
    columnChanged: ObservableValue<boolean>;
    /**
     * user focus was changed to the Card in another doing/done column
     */
    doneChanged?: ObservableValue<boolean>;
    /**
     * user focus was changed to the Card in another swimlane
     */
    rowChanged?: ObservableValue<boolean>;
}

const boardNewItemTypeStorageKey = "{0}-new-item-callout-witType";

/**
 * Component that renders a "main" column, which is either incoming or outgoing
 */
export const MainBoardColumn: React.FunctionComponent<IMainBoardColumnProps> = ({
    backlogLevelWorkItemTypeColorAndIcons,
    boardId,
    className,
    columnLayout,
    filteredIds,
    enabledWorkItemTypes,
    onAddItem,
    onPageAdditionalItems,
    wrapColumn,
    scrollableData,
    ...props
}) => {
    const pageContext = usePageContext();

    // wrap so functions always access current filtered ids
    const [combinedFilteredIdsState, updateColumnFilteredIds] = useCombinedSets(filteredIds);
    const combinedFilteredIds = React.useRef(combinedFilteredIdsState);
    combinedFilteredIds.current = combinedFilteredIdsState;

    const [itemsFilteredMessage, setItemsFilteredMessage] = React.useState<string | undefined>(undefined);
    React.useEffect(() => {
        // reset the message whenever the filter is updated
        if (itemsFilteredMessage) {
            setItemsFilteredMessage(undefined);
        }
    }, [combinedFilteredIds.current]);

    const [isLoading] = useObservable(false);
    const searchValue = React.useRef<string | undefined>(undefined);

    // Load in the next 200 items on the user's initial search
    const hasLoadedFirstPage = React.useRef(false);

    const columnFilterManager = columnLayout.columnFilterManager;
    useSubscription(
        columnLayout.workItems,
        () => {
            if (!columnFilterManager?.isFiltering()) {
                return;
            }

            const columnFilteredIds = new Set(columnFilterManager.filter(pageContext));
            updateColumnFilteredIds(columnFilteredIds);
        },
        [columnFilterManager, filteredIds]
    );

    const getFilteredItemCount = (filteredIds?: Set<number>) => {
        if (!filteredIds) {
            filteredIds = combinedFilteredIds.current;
        }

        return filteredIds ? columnLayout.workItems.value.filter(w => filteredIds!.has(w.value.id)).length : undefined;
    };

    const onLoadWorkItems = async (): Promise<void> => {
        if (!onPageAdditionalItems || !columnLayout.hasPartialItems?.value) {
            return;
        }

        const visibleItemCountBeforeLoad = getFilteredItemCount();
        hasLoadedFirstPage.current = true;

        isLoading.value = true;
        await onPageAdditionalItems(columnLayout, searchValue.current);
        isLoading.value = false;

        // only show a message if filter is active
        if (combinedFilteredIds.current) {
            const visibleItemCount = getFilteredItemCount();
            const searchedItemCount = columnLayout.workItems.length;
            if (columnLayout.hasPartialItems?.value) {
                // more items to fetch, give the user a count of loaded items matching the current filter and total loaded items
                setItemsFilteredMessage(format(Resources.LoadedItemsWithFilterMessage, visibleItemCount, searchedItemCount));
            } else if (visibleItemCount === visibleItemCountBeforeLoad) {
                // only show when the last batch of loaded items doesn't have any filter matches
                setItemsFilteredMessage(Resources.LoadedAllItemsWithFilterMessage);
            }
        }
    };

    const onSearch = async (searchText: string | undefined) => {
        const columnFilteredIds = searchText ? new Set(updateFilterSearchText(pageContext, searchText, columnFilterManager)) : undefined;
        searchValue.current = searchText;
        const filteredIds = updateColumnFilteredIds(columnFilteredIds);
        if (!hasLoadedFirstPage.current) {
            hasLoadedFirstPage.current = true;
            await onLoadWorkItems();
        }

        // announce the number of matches for visually impaired users
        const workItemCount = columnLayout.workItems.length;
        const filteredItemCount = getFilteredItemCount(filteredIds);
        announce(format(Resources.FilteredItemsAnnouncement, filteredItemCount !== undefined ? filteredItemCount : workItemCount));
    };

    const getLocalStorageKeyForWorkItemType = boardId ? () => format(boardNewItemTypeStorageKey, boardId) : undefined;
    const renderShowMoreButton = () => (
        <ShowMoreItemsButton
            hasPartialItems={columnLayout.hasPartialItems}
            isLoading={isLoading}
            message={itemsFilteredMessage}
            onClick={onLoadWorkItems}
        />
    );

    const kanbanBoardService = pageContext.getService<IKanbanBoardService>("IKanbanBoardService");

    return (
        <Observer expanded={columnLayout.expanded}>
            {({ expanded }) => {
                const collapsed = expanded === false;
                let columnContent = (
                    <BoardColumn
                        className={css(collapsed && "collapsed")}
                        collapsed={collapsed}
                        columnLayout={columnLayout}
                        filteredIds={combinedFilteredIds.current}
                        onRenderAdditionalContent={renderShowMoreButton}
                        scrollableData={scrollableData}
                        {...props}
                    >
                        {collapsed ? (
                            <span className="collapsed-column-header">{columnLayout.name}</span>
                        ) : (
                            <>
                                {columnLayout.columnType === BoardColumnType.Incoming && (
                                    <span className="flex-row flex-center justify-space-between margin-8 rhythm-horizontal-8">
                                        {onAddItem && (
                                            <Button
                                                className="add-new-item-button board-column-button-with-text flex-noshrink"
                                                iconProps={{ iconName: "Add", size: IconSize.small }}
                                                onClick={event => {
                                                    const layoutService = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                                                    let workItemTypeColorAndIcon: WorkItemTypeColorAndIcon[] =
                                                        backlogLevelWorkItemTypeColorAndIcons ?? [];
                                                    if (
                                                        enabledWorkItemTypes &&
                                                        enabledWorkItemTypes.size > 0 &&
                                                        backlogLevelWorkItemTypeColorAndIcons
                                                    ) {
                                                        workItemTypeColorAndIcon = workItemTypeColorAndIcon.filter(
                                                            (colorAndIcon: WorkItemTypeColorAndIcon) =>
                                                                enabledWorkItemTypes.has(colorAndIcon.workItemTypeName)
                                                        );
                                                    }
                                                    layoutService.renderCallout(onDismiss => {
                                                        return (
                                                            <NewItemCallout
                                                                allowedPositions={["top"]}
                                                                anchor={event.currentTarget}
                                                                colorAndIcons={workItemTypeColorAndIcon ?? []}
                                                                getLocalStorageKeyForWorkItemType={getLocalStorageKeyForWorkItemType}
                                                                onAddItem={onAddItem}
                                                                onDismiss={onDismiss}
                                                                getBacklogDefaultWorkItemTypeNameFetcher={{ service: kanbanBoardService }}
                                                            />
                                                        );
                                                    });
                                                }}
                                                text={Resources.NewItemButton}
                                            />
                                        )}
                                        <ExpandableSearchButton
                                            buttonClassName="board-column-button"
                                            onSearch={onSearch}
                                            textFieldContainerClassName="board-column-search-text-field-container"
                                            textFieldInputClassName="board-column-search-text-field"
                                        />
                                    </span>
                                )}
                            </>
                        )}
                    </BoardColumn>
                );

                if (wrapColumn) {
                    columnContent = <div className={css("flex-row main-column-container", collapsed && "collapsed")}>{columnContent}</div>;
                }

                return columnContent;
            }}
        </Observer>
    );
};

export const SplitBoardColumn: React.FunctionComponent<IBoardColumnProps> = ({ className, columnLayout, scrollableData, ...props }) => {
    return columnLayout.isDone ? (
        <BoardColumn
            className={css(className, "split-right")}
            columnLayout={columnLayout}
            doneColumn={true}
            scrollableData={scrollableData}
            {...props}
        >
            <span className="padding-8">{Resources.DoneColumnTitle}</span>
        </BoardColumn>
    ) : (
        <BoardColumn
            className={css(className, "split-left")}
            columnLayout={columnLayout}
            doneColumn={false}
            scrollableData={scrollableData}
            {...props}
        >
            <span className="padding-8">{Resources.DoingColumnTitle}</span>
        </BoardColumn>
    );
};

export interface IBoardColumnProps extends Pick<BoardLayout, "cardLayout" | "tagStyles" | "workItemTypeColorAndIconsMap"> {
    className?: string;
    collapsed?: boolean;
    columnLayout: BoardColumnLayout;
    /**
     * True if done column, false if doing column
     * @default false
     */
    doneColumn?: boolean;
    filteredIds?: Set<number>;
    onCardDrag: (e: PointerEvent, draggingElement: HTMLElement, mode: DragMode, position: IPosition) => undefined;
    onCardKeyDown?: (event: React.KeyboardEvent<HTMLElement>, item: IObservableBoardsWorkItem, column: BoardColumnLayout) => void;
    onRenderAdditionalContent?: (props: IBoardColumnProps) => JSX.Element;
    columnChanged: ObservableValue<boolean>;
    doneChanged?: ObservableValue<boolean>;
    rowChanged?: ObservableValue<boolean>;
    onCardFocus?: (columnLayout: BoardColumnLayout, rowName?: string) => void;
    rowName?: string;
    scrollableData?: ScrollableData;
}

export const BoardColumn: React.FunctionComponent<IBoardColumnProps> = props => {
    const {
        cardLayout,
        children,
        className,
        collapsed,
        columnLayout,
        doneColumn = false,
        filteredIds,
        onCardDrag,
        onCardKeyDown,
        onRenderAdditionalContent,
        tagStyles,
        workItemTypeColorAndIconsMap,
        columnChanged,
        doneChanged,
        rowChanged,
        onCardFocus,
        rowName,
        scrollableData
    } = props;
    const pageContext = usePageContext();
    const onKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLElement>, item: IObservableBoardsWorkItem) => {
            onCardKeyDown?.(event, item, columnLayout);
        },
        [onCardKeyDown, columnLayout]
    );

    const onFocus = React.useCallback(() => {
        onCardFocus?.(columnLayout, rowName);
    }, [onCardFocus, columnLayout, rowName]);

    return (
        <div
            className={css("flex-column flex-grow kanban-board-column padding-bottom-8", className)}
            data-column={columnLayout.id}
            data-done={doneColumn}
        >
            {children}
            {!collapsed && (
                <>
                    <Observer items={columnLayout.workItems}>
                        {({ items }) =>
                            items.map(item => (
                                <BoardCard
                                    className={!filteredIds || filteredIds.has(item.value.id) ? undefined : "hidden"}
                                    key={item.value.id}
                                    cardLayouts={cardLayout}
                                    item={item}
                                    onCardDrag={onCardDrag}
                                    onKeyDown={onKeyDown}
                                    onFocus={onFocus}
                                    pageContext={pageContext}
                                    workItemTypeColorAndIconsMap={workItemTypeColorAndIconsMap}
                                    tagStyles={tagStyles}
                                    columnName={columnLayout.name}
                                    columnIsDone={columnLayout.isDone}
                                    columnIsSplited={columnLayout.isSplit}
                                    rowName={rowName}
                                    columnChanged={columnChanged}
                                    rowChanged={rowChanged}
                                    scrollableData={scrollableData}
                                    doneChanged={doneChanged}
                                />
                            ))
                        }
                    </Observer>
                    {onRenderAdditionalContent && onRenderAdditionalContent(props)}
                </>
            )}
        </div>
    );
};

export interface IBoardColumnsHeaderProps {
    columns: BoardColumnLayout[];
    onSaveToggle: (expanded: boolean, rowId: string) => void;
}

export const BoardColumnsHeader: React.FunctionComponent<IBoardColumnsHeaderProps> = ({ columns, onSaveToggle }) => {
    const incomingColumn = columns.find(c => isIncoming(c));
    const outgoingColumn = columns.find(c => isOutgoing(c));
    return (
        <div className={css("flex-row kanban-board-column-header-container margin-bottom-4 font-size-ml")}>
            <Observer incomingColumnExpanded={incomingColumn?.expanded} outgoingColumnExpanded={outgoingColumn?.expanded}>
                {() => {
                    // count the number of expanded columns + the number of split columns (they take the width of two columns)
                    const expandedColumnCount = columns.filter(c => !c.expanded || c.expanded.value).length + columns.filter(c => c.isSplit).length;
                    return columns.map(columnLayout => (
                        <Observer workItems={columnLayout.workItems} key={`header-${columnLayout.id}`}>
                            {({ workItems }) => {
                                const maxItemCount = columnLayout.itemLimit;
                                const currentItemCount = workItems.length;
                                const collapsible = !!columnLayout.expanded;
                                const collapsed = collapsible && columnLayout.expanded!.value === false;
                                const onClick = collapsible
                                    ? () => {
                                          columnLayout.expanded!.value = !columnLayout.expanded!.value;
                                          onSaveToggle(columnLayout.expanded!.value, columnLayout.id);
                                      }
                                    : undefined;

                                let style: React.CSSProperties | undefined;
                                if (!collapsed) {
                                    let flexBasis = 100 / expandedColumnCount;
                                    if (columnLayout.isSplit) {
                                        flexBasis = flexBasis * 2;
                                    }

                                    style = { flexBasis: `${flexBasis}%` };
                                }

                                return (
                                    <span
                                        className={css(
                                            "flex-row flex-grow kanban-board-column-header",
                                            collapsed && "justify-center collapsed",
                                            collapsible && "collapsible",
                                            columnLayout.isSplit && "split-column-header"
                                        )}
                                        onClick={onClick}
                                        style={style}
                                        aria-label={columnLayout.name}
                                    >
                                        {!collapsed && (
                                            <span className="flex-row flex-grow justify-space-between">
                                                <BoardColumnName columnLayout={columnLayout} />
                                                {columnLayout.columnType === BoardColumnType.InProgress && (
                                                    <BoardColumnItemCount currentItemCount={currentItemCount} maxItemCount={maxItemCount} />
                                                )}
                                            </span>
                                        )}
                                        {collapsible && (
                                            <Icon
                                                className="font-weight-semibold"
                                                iconName={collapsed ? "ChevronRight" : "ChevronLeft"}
                                                size={IconSize.small}
                                            />
                                        )}
                                    </span>
                                );
                            }}
                        </Observer>
                    ));
                }}
            </Observer>
        </div>
    );
};

function BoardColumnName({ columnLayout }: { columnLayout: BoardColumnLayout }) {
    const boardService = useService<IKanbanBoardService>("IKanbanBoardService");

    return (
        <div className="flex-row">
            <ClickEditField
                value={columnLayout.name}
                onCommit={newName => boardService.renameColumn(columnLayout.id, newName)}
                error={boardService.getColumnNameError(columnLayout.id)}
            />
            {columnLayout.description ? (
                <Icon
                    iconName="Info"
                    className="margin-left-4"
                    tooltipProps={{
                        className: "board-column-description-tooltip subtle-border",
                        renderContent: () => <MarkdownPreview value={columnLayout.description} />
                    }}
                />
            ) : null}
        </div>
    );
}

interface IBoardColumnItemCountProps {
    currentItemCount: number;
    maxItemCount: number;
}

function BoardColumnItemCount({ currentItemCount, maxItemCount }: IBoardColumnItemCountProps) {
    const hasMaximum = maxItemCount > 0;
    return (
        <span>
            <span className={css("board-column-item-count font-size-l", hasMaximum && currentItemCount > maxItemCount && "over-limit")}>
                {currentItemCount}
            </span>
            {hasMaximum && (
                <>
                    <span className="font-size-m">/</span>
                    <span className="font-size">{maxItemCount}</span>
                </>
            )}
        </span>
    );
}

interface IExpandableSearchButton {
    buttonClassName?: string;
    onSearch?: (searchText: string | undefined) => void;
    textFieldContainerClassName?: string;
    textFieldInputClassName?: string;
}

const ExpandableSearchButton: React.FunctionComponent<IExpandableSearchButton> = ({
    buttonClassName,
    onSearch,
    textFieldContainerClassName,
    textFieldInputClassName
}) => {
    const [expanded, setExpanded] = React.useState(false);
    const [text] = useObservable<string | undefined>(undefined);
    const textFieldRef = React.useRef<TextField>(null);

    const onChange = (value: string | undefined) => {
        text.value = value;
        onSearch?.(text.value);
    };

    const clear = () => {
        onChange(undefined);
        setExpanded(false);
    };

    React.useEffect(() => {
        // focus the text field on mount
        if (expanded && textFieldRef.current) {
            textFieldRef.current.focus();
        }
    }, [expanded]);

    return (
        <>
            {!expanded ? (
                <Button
                    ariaLabel={Resources.SearchIncomingWorkItemsButton}
                    className={buttonClassName}
                    iconProps={{ iconName: "Search", size: IconSize.small }}
                    onClick={() => {
                        setExpanded(true);
                    }}
                />
            ) : (
                <TextField
                    containerClassName={textFieldContainerClassName}
                    inputClassName={textFieldInputClassName}
                    onBlur={() => {
                        if (!text.value) {
                            clear();
                        }
                    }}
                    onKeyDown={event => {
                        if (event.key === "Escape") {
                            clear();
                        }
                    }}
                    onChange={(event, value) => onChange(value)}
                    suffixIconProps={{ iconName: "Cancel", onClick: clear, className: "cursor-pointer" }}
                    ref={textFieldRef}
                    value={text}
                />
            )}
        </>
    );
};

interface IShowMoreItemsButtonProps extends Pick<IButtonProps, "onClick"> {
    hasPartialItems?: IObservableValue<boolean>;
    isLoading: IObservableValue<boolean>;
    message?: string;
}

const ShowMoreItemsButton: React.FunctionComponent<IShowMoreItemsButtonProps> = ({ hasPartialItems, isLoading, message, onClick }) => {
    return (
        <div className="flex-column margin-top-8 margin-horizontal-8">
            <Observer hasPartialItems={hasPartialItems} isLoading={isLoading}>
                {({ hasPartialItems, isLoading }) => {
                    if (!hasPartialItems) {
                        return null;
                    }

                    return isLoading ? <Spinner /> : <Button onClick={onClick} text={Resources.ShowMoreItemsButton} />;
                }}
            </Observer>
            {message && <span className="margin-top-8">{message}</span>}
        </div>
    );
};

/**
 * Maintain a set intersected from a set passed in through args and a local set
 * @returns [0]: Set that is intersection of local set and set from args
 *          [1]: Function for updating local set
 */
function useCombinedSets<T>(setFromProps: Set<T> | undefined): [Set<T> | undefined, (updatedSet?: Set<T>) => Set<T> | undefined] {
    const [combinedSet, setCombinedSets] = React.useState<Set<T> | undefined>(setFromProps);
    const localSet = React.useRef<Set<T> | undefined>(undefined);
    const updateLocalSet = (updatedSet?: Set<T>): Set<T> | undefined => {
        localSet.current = updatedSet;
        const intersection = intersectSets(setFromProps, localSet.current);
        setCombinedSets(intersection);
        return intersection;
    };

    React.useEffect(() => {
        const intersection = intersectSets(setFromProps, localSet.current);
        setCombinedSets(intersection);
    }, [setFromProps]);

    return [combinedSet, updateLocalSet];
}

function updateFilterSearchText(pageContext: IVssPageContext, searchText: string, filterManager?: FilterManager): number[] {
    if (!filterManager) {
        return [];
    }

    filterManager.setFilter(TextFilterProvider.PROVIDER_TYPE, { values: [searchText] });
    return filterManager.filter(pageContext);
}

function intersectSets<T>(set1?: Set<T>, set2?: Set<T>): Set<T> | undefined {
    if (!set1 && !set2) {
        return;
    }

    if (set1 && !set2) {
        return set1;
    }

    if (!set1 && set2) {
        return set2;
    }

    // add all items that are in both sets
    const combinedSet = new Set<T>();
    for (const item of set1!) {
        if (set2!.has(item)) {
            combinedSet.add(item);
        }
    }

    return combinedSet;
}
